import { useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { router } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusMultiSelect } from './StatusMultiSelect';

interface WorkflowFiltersProps {
  statusFilter: number[];
  workflowFilter: string;
  onStatusFilterChange: (statuses: number[]) => void;
  onWorkflowFilterChange: (value: string) => void;
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
}

export const WorkflowFilters = ({
  statusFilter,
  workflowFilter,
  onStatusFilterChange,
  onWorkflowFilterChange,
  onClearFilters,
  totalCount,
  filteredCount,
}: WorkflowFiltersProps) => {
  const [workflowIdSearch, setWorkflowIdSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const hasActiveFilters = statusFilter.length > 0 || workflowFilter;

  // Navigate directly to workflow run page
  const handleWorkflowIdSearch = () => {
    const id = workflowIdSearch.trim();
    if (!id) return;

    setIsSearching(true);
    router.visit(`/workflows?workflow_id=${encodeURIComponent(id)}`, {
      preserveState: false,
      onFinish: () => setIsSearching(false),
    });
  };

  const handleClearWorkflowIdSearch = () => {
    setWorkflowIdSearch('');
    router.visit('/workflows', { preserveState: false });
  };

  return (
    <div className="flex flex-col gap-4 mb-6">
      {/* Filter Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <StatusMultiSelect
          selectedStatuses={statusFilter}
          onSelectionChange={onStatusFilterChange}
        />

        {/* Client-side filter for loaded data */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter loaded results..."
            value={workflowFilter}
            onChange={(e) => onWorkflowFilterChange(e.target.value)}
            className="pl-9 pr-9 w-[200px]"
          />
          {workflowFilter && (
            <button
              onClick={() => onWorkflowFilterChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Server-side search for workflow ID */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Input
              placeholder="Go to workflow ID..."
              value={workflowIdSearch}
              onChange={(e) => setWorkflowIdSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleWorkflowIdSearch()}
              className="w-[320px] pr-9"
            />
            {workflowIdSearch && (
              <button
                onClick={handleClearWorkflowIdSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleWorkflowIdSearch}
            disabled={isSearching || !workflowIdSearch.trim()}
          >
            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Go'}
          </Button>
        </div>

        {hasActiveFilters && (
          <Button variant="outline" onClick={onClearFilters} className="text-muted-foreground">
            Clear Filters
          </Button>
        )}
      </div>

      {/* Results Summary */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredCount} of {totalCount} workflows
        {hasActiveFilters && <span className="ml-2 text-primary">(filtered)</span>}
      </div>
    </div>
  );
};
